// Matches a string that is entirely digits (a plain non-negative integer, e.g. "2"),
// so range-token values like "1-3" or "11+" are preserved as strings, not coerced to ints.
const DIGITS_ONLY = /^\d+$/;

export default class UrlService {
  /**
   * Returns the colon prefixed value for a given url matcher.
   * getPathParam('/fonts/tags/:tag') for the url "/fonts/tags/something" returns 'something'
   */
  static getPathParam(matcher) {
    const matchParts = matcher.split('/');
    const urlParts = this.getWindowLocation().pathname.split('/');
    while (matchParts.length) {
      const matchPart = matchParts.shift();
      const urlPart = urlParts.shift();
      // If the match part is empty, then it's the root.
      if (matchPart === urlPart) {
        continue;
      }
      // If the match part starts with a colon, then it's a matcher.
      if (matchPart.indexOf(':') === 0) {
        return urlPart;
      }
      // If the match part doesn't start with a colon, then it's not a matcher.
      if (matchPart !== urlPart) {
        return;
      }
    }
  }

  /**
   * Returns the search value for the key.
   *
   * @param {String} key
   * @returns {String}
   */
  static getSearchParam(key) {
    return this.parseSearchParams(this.getWindowLocation().search)[key];
  }

  /**
   * Returns the parsed search portion of the url as an object.
   * @returns {{}}
   */
  static getSearchParams() {
    return this.parseSearchParams(this.getWindowLocation().search);
  }

  static getSerializedSearchParams(searchParams) {
    const searchParamsWithoutNullValues = Object.fromEntries(Object.entries(searchParams).filter(([, value]) => value !== null));
    // Keep commas literal so multi-value params (e.g. group=a,b) stay compact/readable.
    return new URLSearchParams(searchParamsWithoutNullValues).toString().replace(/%2C/g, ',');
  }

  /**
   * Gets the window location. This has its own method so it can be stubbed in tests.
   *
   * @returns {Location}
   */
  static getWindowLocation() {
    return window.location;
  }

  /**
   * Parses the search portion of a url and returns an object that
   * represents the search string.
   *
   * @param {String} searchString
   * @return {{}}
   */
  static parseSearchParams(searchString) {
    const rawKeyValuePairs = searchString.replace(/^\?/, '').split('&');
    return rawKeyValuePairs.sort().reduce((accumulator, item) => {
      const parts = item.split('=');
      if (parts[0]) {
        accumulator[parts[0]] = this._parseUrlValue(parts[1]);
      }

      return accumulator;
    }, {});
  }

  /**
   * Update the current path using history.pushState.
   *
   * @param {Object} stateObj
   * @param {String} title
   * @param {String} url
   */
  static pushState(stateObj, title, url) {
    history.pushState(stateObj, title, url);
  }

  /**
   * Remove every occurrence of the decoded search parameter key from the URL.
   * Safely decodes keys only, preserving unrelated query pairs byte-for-byte.
   * @param {String} key
   */
  static removeSearchParam(key) {
    const location = this.getWindowLocation();
    const search = location.search.replace(/^\?/, '').split('&').filter(pair => {
      const separator = pair.indexOf('=');
      const rawKey = separator === -1 ? pair : pair.slice(0, separator);
      const decodedKey = this.safeDecodeURIComponent(rawKey.replace(/\+/g, ' '));
      return decodedKey !== key;
    }).join('&');
    return this.updatePath((search ? '?' + search : location.pathname) + (location.hash || ''));
  }

  /**
   * Replace the existing search with the params passed in.
   * @param {{}} searchParams
   */
  static setSearchParams(searchParams) {
    const serialized = this.getSerializedSearchParams(searchParams);
    const location = this.getWindowLocation();
    let path = serialized.length === 0 ? location.pathname : '?' + serialized;
    if (location.hash) {
      path += location.hash;
    }
    history.replaceState(null, null, path);
  }

  /**
   * Parses a search string value and returns either an unencoded string
   * or a number, depending on the parsed value.
   * Malformed percent escapes are preserved as literal text.
   *
   * @private
   * @param {String} value
   * @return {Integer|String}
   */
  static _parseUrlValue(value) {
    const parsed = parseInt(value, 10);
    if (DIGITS_ONLY.test(value) && !Number.isNaN(parsed)) {
      return parsed;
    }

    return this.safeDecodeURIComponent(value.replace(/\+/g, ' '));
  }

  /**
   * Updates the existing search with search params passed in.
   * @param {{}} newParams
   */
  static updateSearchParams(newParams) {
    let searchParams = this.parseSearchParams(this.getWindowLocation().search);
    searchParams = {...searchParams, ...newParams};
    this.setSearchParams(searchParams);
  }

  /*
   * Updates the current path to the value passed
   * @param {String} newPath
   */
  static updatePath(newPath) {
    history.replaceState(null, null, newPath);
  }

  /**
   * Builds (without navigating) the path + merged search params string.
   * @param {String} pathname
   * @param {{}} newParams
   * @returns {String}
   */
  static buildPathAndSearchParams(pathname, newParams) {
    const location = this.getWindowLocation();
    let searchParams = this.parseSearchParams(location.search);
    searchParams = {...searchParams, ...newParams};
    const serialized = this.getSerializedSearchParams(searchParams);
    let path = serialized.length === 0 ? pathname : pathname + '?' + serialized;
    if (location.hash) {
      path += location.hash;
    }
    return path;
  }

  /**
   * Builds (without navigating) the current path + merged search params string.
   * @param {{}} newParams
   * @returns {String}
   */
  static buildSearchParams(newParams) {
    return this.buildPathAndSearchParams(this.getWindowLocation().pathname, newParams);
  }

  /**
   * Updates both the current path and search params.
   * @param {String} pathname
   * @param {{}} newParams
   */
  static updatePathAndSearchParams(pathname, newParams) {
    history.replaceState(null, null, this.buildPathAndSearchParams(pathname, newParams));
  }

  /**
   * Returns true if the current path begins with the path passed in.
   */
  static pathStartsWith(path) {
    return this.getWindowLocation().pathname.indexOf(path) === 0;
  }

  /**
   * Returns an array of values for a search param that is a comma-separated list.
   * @param {String} key
   * @returns {Array}
   */
  static splitCsvParam(key) {
    const value = UrlService.getSearchParam(key);
    return value ? String(value).split(',').filter(Boolean) : [];
  }

  static safeDecodeURIComponent(value) {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
}
